import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db, COLLECTIONS } from '@/lib/firebase'
import { getDocumentCacheFirst } from '@/lib/firestoreCache'
import type { StoreSettings } from '@/types'
import { COMING_SOON_DEFAULTS } from '@/lib/comingSoon'
import { pickPublicStoreSettings } from '@/lib/publicSettings'

const SETTINGS_DOC_ID = 'store'
const PUBLIC_SETTINGS_DOC_ID = 'public'
const PAYMENT_SETTINGS_DOC_ID = 'payment'

export interface PaymentSettings {
  /** @deprecated Legacy — use razorpayDevKeySecret. */
  razorpayKeySecret?: string
  razorpayDevKeySecret?: string
  razorpayProdKeySecret?: string
  smtpPass?: string
  /** Delhivery CMU API token (server-side only). */
  delhiveryApiToken?: string
  /** Registered Delhivery warehouse name (case/space sensitive). */
  delhiveryPickupLocation?: string
  /** Override create-shipment URL (defaults to staging in code). */
  delhiveryCreateShipmentUrl?: string
  updatedAt?: string
}

const defaultSettings: Omit<StoreSettings, 'id' | 'updatedAt'> = {
  storeName: 'Bazm-e-Musk',
  tagline: 'Luxury Attar & Oriental Fragrances',
  email: 'contact@bazmemusk.com',
  phone: '+91 9906857273',
  whatsapp: '+91 9906857273',
  instagram: 'https://instagram.com/bazmemusk',
  address: 'Kralpora, Kupwara, Jammu and Kashmir',
  shippingCharge: 50,
  freeShippingThreshold: 1500,
  premiumPackagingEnabled: true,
  premiumPackagingCharge: 149,
  premiumPackagingDescription: '',
  codEnabled: true,
  razorpayEnabled: true,
  razorpayEnvironment: 'development',
  razorpayKeyId: '',
  razorpayDevKeyId: '',
  razorpayProdKeyId: '',
  currency: 'INR',
  lowStockThreshold: 5,
  returnRefundPolicy: '',
  termsAndConditions: '',
  privacyPolicy: '',
  comingSoonEnabled: COMING_SOON_DEFAULTS.comingSoonEnabled,
  comingSoonLaunchAt: COMING_SOON_DEFAULTS.comingSoonLaunchAt,
  comingSoonProgressStartAt: COMING_SOON_DEFAULTS.comingSoonProgressStartAt,
  orderEmailsEnabled: false,
  smtpHost: '',
  smtpPort: 465,
  smtpUser: '',
  smtpFrom: '',
  orderEmailSubject: 'Your {{storeName}} order {{orderNumber}}',
  orderAdminEmail: '',
  showCategoriesOnHomepage: true,
  returnWindowHours: 24,
  returnWindowMinutes: 0,
  ga4PropertyId: '',
}

export async function fetchSettings(): Promise<StoreSettings> {
  const snap = await getDoc(doc(db, COLLECTIONS.settings, SETTINGS_DOC_ID))
  if (!snap.exists()) {
    return {
      id: SETTINGS_DOC_ID,
      ...defaultSettings,
      updatedAt: new Date().toISOString(),
    }
  }
  return {
    id: SETTINGS_DOC_ID,
    ...defaultSettings,
    ...snap.data(),
  } as StoreSettings
}

/** Storefront-safe settings (no admin emails, SMTP, or internal config). */
export async function fetchPublicSettings(): Promise<StoreSettings> {
  const snap = await getDocumentCacheFirst(
    doc(db, COLLECTIONS.settings, PUBLIC_SETTINGS_DOC_ID)
  )
  if (!snap.exists()) {
    return {
      id: PUBLIC_SETTINGS_DOC_ID,
      ...defaultSettings,
      updatedAt: new Date().toISOString(),
    }
  }
  return {
    id: PUBLIC_SETTINGS_DOC_ID,
    ...defaultSettings,
    ...snap.data(),
  } as StoreSettings
}

/** Admin: copy full store settings into the public storefront document. */
export async function syncPublicStoreSettings(
  store?: Partial<StoreSettings>
): Promise<void> {
  const source = store ?? (await fetchSettings())
  const publicData = pickPublicStoreSettings(source)
  await setDoc(
    doc(db, COLLECTIONS.settings, PUBLIC_SETTINGS_DOC_ID),
    {
      ...publicData,
      updatedAt: new Date().toISOString(),
    },
    { merge: true }
  )
}

export async function updateSettings(
  data: Partial<StoreSettings>
): Promise<void> {
  const { id: _id, updatedAt: _u, ...rest } = data
  if (Object.keys(rest).length === 0) return

  const now = new Date().toISOString()
  await setDoc(
    doc(db, COLLECTIONS.settings, SETTINGS_DOC_ID),
    {
      ...rest,
      updatedAt: now,
    },
    { merge: true }
  )

  const merged = {
    ...(await fetchSettings()),
    ...rest,
    updatedAt: now,
  }
  await syncPublicStoreSettings(merged)
}

export async function fetchPaymentSettings(): Promise<PaymentSettings> {
  const snap = await getDoc(
    doc(db, COLLECTIONS.settings, PAYMENT_SETTINGS_DOC_ID)
  )
  if (!snap.exists()) return {}
  return snap.data() as PaymentSettings
}

export async function updatePaymentSettings(
  data: PaymentSettings
): Promise<void> {
  await setDoc(
    doc(db, COLLECTIONS.settings, PAYMENT_SETTINGS_DOC_ID),
    {
      ...data,
      updatedAt: new Date().toISOString(),
    },
    { merge: true }
  )
}

export { defaultSettings, PAYMENT_SETTINGS_DOC_ID, PUBLIC_SETTINGS_DOC_ID }
